import { PlatformContextType } from "@wingspanhq/users/dist/lib/interfaces";
import { UserAccountType } from "@wingspanhq/users/dist/lib/interfaces/newUser";
import { useCustomization } from "../../modules/customization";
import {
  IFeatureFlags,
  useQueryFeatureFlags
} from "@hooks/useQueryFeatureFlags";
import { useShowBatchImportsNav } from "../../userFeatures/useShowBatchImportsNav";
import { useShowCustomFieldsNav } from "../../userFeatures/useShowCustomFieldsNav";
import { useShowGetPaidNav } from "../../userFeatures/useShowGetPaidNav";
import { useShowNewCollaboratorPaymentNav } from "../../userFeatures/useShowNewCollaboratorPaymentNav";
import { useShowNewOneTimeInvoiceNav } from "../../userFeatures/useShowNewOneTimeInvoiceNav";
import { useShowNewRecurringInvoicesNav } from "../../userFeatures/useShowNewRecurringInvoicesNav";
import { useShowPayeableAndPayrollSettingsNav } from "../../userFeatures/useShowPayeableAndPayrollSettingsNav";
import { useShowPaymentNav } from "../../userFeatures/useShowPaymentNav";
import { useShowPayoutNav } from "../../userFeatures/useShowPayoutNav";
import { useShowSendPaymentsNav } from "../../userFeatures/useShowSendPaymentsNav";
import { queriesWorkDefinitions, queriesWorkLogs } from "@query";
import { useSession } from "@hooks/useSession";
import { useAccountAccess } from "../../v3/hooks/useAccountAccess";

export type DashboardFocusedFeatureContext = {
  experiments: IFeatureFlags;
  hardcoded: {
    showBatchImportsNav: boolean;
    showCustomFieldsNav: boolean;
    showGetPaidNav: boolean;
    showNewCollaboratorPaymentNav: boolean;
    showNewOneTimeInvoiceNav: boolean;
    showNewRecurringInvoicesNav: boolean;
    showPayableAndPayrollSettingsNav: boolean;
    showPaymentNav: boolean;
    showPayoutNav: boolean;
    showSendPaymentsNav: boolean;
  };
  session: {
    isImpersonated: boolean;
  };
  user: {
    country: string;
    contexts: PlatformContextType[];
    userType?: UserAccountType;
    labels?: { [label: string]: string | boolean | null };
    payersCount: number;
    /** This account logs work for a client — the contractor side of work logging. */
    hasWorkLogs: boolean;
    /** This account defines work to be logged — the client side of work logging. */
    hasWorkDefinitions: boolean;
  };
  access: {
    hasScope: (scope: string) => boolean;
  };
  customization: ReturnType<typeof useCustomization>;
};

export const useGetDashboardFocusedFeaturesContext =
  (): DashboardFocusedFeatureContext | null => {
    const featureFlagsQuery = useQueryFeatureFlags({
      refetchOnMount: false
    });

    // The v1 user/member/payer-rows-summary/work-definitions reads were removed
    // during the v3 migration — they threw NotMigrated and blanked the entire
    // nav (this builder returned null when their data was missing). Access is
    // now sourced from the v3 authorizations (useAccountAccess); the remaining
    // `user.*` fields below could not be matched to v3 yet and are defaulted.

    const customization = useCustomization();

    const hardcoded = {
      showBatchImportsNav: useShowBatchImportsNav(),
      showCustomFieldsNav: useShowCustomFieldsNav(),
      showGetPaidNav: useShowGetPaidNav(),
      showNewCollaboratorPaymentNav: useShowNewCollaboratorPaymentNav(),
      showNewOneTimeInvoiceNav: useShowNewOneTimeInvoiceNav(),
      showNewRecurringInvoicesNav: useShowNewRecurringInvoicesNav(),
      showPayableAndPayrollSettingsNav: useShowPayeableAndPayrollSettingsNav(),
      showPaymentNav: useShowPaymentNav(),
      showPayoutNav: useShowPayoutNav(),
      showSendPaymentsNav: useShowSendPaymentsNav()
    };

    const { isImpersonated, hasScope } = useAccountAccess();
    const { accountId } = useSession();

    // Work-logging nav gating. v1 read two counts off the payer-rows summary;
    // v3 projects neither, so each side is gated on the resource that defines
    // it. One row is all the question needs — "does this account do work
    // logging at all" — so both reads ask for a single-item page.
    //
    // Both run once per session for every user: `hasScope` answers true until
    // the authorizations read lands (no grants reads as full access), so it
    // cannot hold the request back, and `staleTime: Infinity` means it is not
    // repeated. It stays on `enabled` because it is the correct predicate, and
    // `features.ts` applies it again where the nav actually renders.
    //
    // Deliberately NOT `retry: false`: these reads decide whether a feature is
    // visible at all, and with `refetchOnMount: false` a single transient 5xx
    // would hide the entry for the whole session — the very symptom PLA-3637
    // was filed for. The shared bounded retry is the right behavior here.
    const queryWorkDefinitions =
      queriesWorkDefinitions.listWorkDefinitions.useQuery(
        { page: { size: 1 } },
        { enabled: hasScope("payments.workDefinition") }
      );

    const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQuery(
      { filter: { payeeAccountId: { eq: accountId } }, page: { size: 1 } },
      { enabled: !!accountId && hasScope("payments.workLog") }
    );

    if (!featureFlagsQuery.data) {
      return null;
    }

    const experiments = featureFlagsQuery.data;

    return {
      hardcoded,
      experiments,
      user: {
        // country — was v1 member.profile.address.country. No v3 wiring yet;
        // defaulted to US (it only gates the tax-filing section, which also
        // depends on the enterprise/context flags below).
        country: "US",
        // contexts — was v1 user.contexts (Payer/Payee/TaxOnly). No v3
        // equivalent; nav should rely on access.hasScope instead.
        contexts: [],
        // userType — was v1 user.settings.userAccountType (enterprise). No v3
        // equivalent.
        userType: undefined,
        // labels — was v1 user.labels. No v3 equivalent.
        labels: undefined,
        // payersCount — was the v1 payer-rows summary. Derivable from v3
        // queriesPayers.listPayers later; defaulted for now.
        payersCount: 0,
        // KNOWN GAP: this asks "has work logs", where v1 asked "has a client who
        // does work logging" — so a contractor with a live assignment and no
        // logs yet sees no nav entry, and reaches the create screen only by URL.
        // v3 has no cheap read for the v1 question: `searchPayerRows` filtered on
        // `engagements.isWorkLoggingEnabled` 503s until ARC-1062, and the
        // work-logging config is per-payer, so answering it means enumerating
        // clients first. Swap this over once ARC-1062 lands.
        hasWorkLogs: (queryWorkLogs.data?.data?.length ?? 0) > 0,
        hasWorkDefinitions: (queryWorkDefinitions.data?.data?.length ?? 0) > 0
      },
      session: {
        isImpersonated
      },
      access: {
        hasScope
      },
      customization
    };
  };
